import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useUserId, useUserEmail } from './UserAuthContext';

const API_URL = window.location.origin;

const PageVisibilityContext = createContext({
  hiddenPages: [],
  togglePage: () => {},
  isHidden: () => false,
});

export function PageVisibilityProvider({ children }) {
  const userId = useUserId();
  const userEmail = useUserEmail();
  // Counterfind is a demo tenant where every role should see every sidebar
  // page. The role-scoped userId fragments the localStorage cache (each role
  // gets its own key), so stale entries from earlier sessions can hide pages
  // on the first paint before the DB sync overwrites them. Short-circuiting
  // hidden-pages for this tenant eliminates the flicker entirely.
  const isCounterfind = (userEmail || '').toLowerCase() === 'counterfind@operatr.com';
  const storageKey = userId ? `operatr_hidden_pages_${userId}` : 'operatr_hidden_pages';
  const syncTimeoutRef = useRef(null);
  // For Counterfind we suppress hidden-pages only until the DB sync has
  // completed (or any explicit toggle has happened). This kills the initial
  // sidebar flicker caused by stale per-role localStorage cache while still
  // letting the admin actually hide/show pages once settings are loaded.
  const [hydrated, setHydrated] = useState(false);

  const [hiddenPages, setHiddenPages] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // When userId becomes available, load from DB (authoritative source for admin control)
  useEffect(() => {
    if (!userId) return;
    const localKey = `operatr_hidden_pages_${userId}`;

    const loadFromDB = async () => {
      try {
        const res = await fetch(`${API_URL}/api/user-addons/page-settings?user_id=${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error('DB fetch failed');
        const data = await res.json();
        const dbPages = data.hidden_pages || [];
        const localRaw = localStorage.getItem(localKey);
        const localPages = localRaw ? JSON.parse(localRaw) : null;

        if (dbPages.length > 0) {
          // DB has authoritative settings (admin set or previously synced) — use DB
          setHiddenPages(dbPages);
          localStorage.setItem(localKey, JSON.stringify(dbPages));
        } else if (localPages && localPages.length > 0) {
          // DB is empty but localStorage has data (e.g. just set during onboarding)
          // Sync localStorage → DB so future loads are consistent
          setHiddenPages(localPages);
          fetch(`${API_URL}/api/user-addons/page-settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId, hidden_pages: localPages })
          }).catch(() => {});
        }
        // If both empty → keep current state (no-op, show all pages)
      } catch {
        // Fallback to localStorage if DB is unreachable
        try {
          const raw = localStorage.getItem(localKey);
          if (raw) setHiddenPages(JSON.parse(raw));
        } catch { /* ignored */ }
      } finally {
        setHydrated(true);
      }
    };

    loadFromDB();
  }, [userId]);

  // Re-read from localStorage when userId changes (login switch)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      setHiddenPages(raw ? JSON.parse(raw) : []);
    } catch {
      setHiddenPages([]);
    }
  }, [storageKey]);

  // Debounced sync to DB
  const syncToDB = useCallback((pages) => {
    if (!userId) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      fetch(`${API_URL}/api/user-addons/page-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, hidden_pages: pages })
      }).catch(() => {}); // silently fail — localStorage is always the cache
    }, 800);
  }, [userId]);

  const togglePage = useCallback((path) => {
    setHydrated(true);
    setHiddenPages(prev => {
      const next = prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path];
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignored */ }
      syncToDB(next);
      return next;
    });
  }, [storageKey, syncToDB]);

  // Allow external set (used by admin panel override)
  const setHiddenPagesExternal = useCallback((pages) => {
    setHydrated(true);
    setHiddenPages(pages);
    try { localStorage.setItem(storageKey, JSON.stringify(pages)); } catch { /* ignored */ }
    syncToDB(pages);
  }, [storageKey, syncToDB]);

  const isHidden = useCallback(
    (path) => ((isCounterfind && !hydrated) ? false : hiddenPages.includes(path)),
    [hiddenPages, isCounterfind, hydrated],
  );

  return (
    <PageVisibilityContext.Provider value={{ hiddenPages, togglePage, isHidden, setHiddenPagesExternal }}>
      {children}
    </PageVisibilityContext.Provider>
  );
}

export function usePageVisibility() {
  return useContext(PageVisibilityContext);
}
